// ============================================================
// ShareModal — ChartFlow
// ============================================================
// Invite collaborators to a song.
// Three sharing modes: View only, Write (fork), Linked write.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useSongStore } from '../../store/songStore';
import {
  inviteCollaborator,
  removeCollaborator,
  getCollaborators,
} from '../../core/cloudSync';
import type { DbCollaborator } from '../../lib/supabase';

type ShareMode = 'view' | 'write' | 'linked';

const MODES: { value: ShareMode; label: string; description: string }[] = [
  {
    value:       'view',
    label:       'View only',
    description: 'Can see the chart but not edit it',
  },
  {
    value:       'write',
    label:       'Write',
    description: 'Gets their own independent copy to edit freely',
  },
  {
    value:       'linked',
    label:       'Linked write',
    description: 'Real-time collaboration — edits sync live both ways',
  },
];

const MODE_LABELS: Record<ShareMode, string> = {
  view:   'View only',
  write:  'Write',
  linked: 'Linked write',
};

interface ShareModalProps {
  onClose: () => void;
}

export function ShareModal({ onClose }: ShareModalProps) {
  const user = useAuthStore((s) => s.user);
  const song = useSongStore((s) => s.song);

  const [collaborators, setCollaborators] = useState<DbCollaborator[]>([]);
  const [email, setEmail]   = useState('');
  const [mode, setMode]     = useState<ShareMode>('view');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadCollaborators = useCallback(async () => {
    const { data } = await getCollaborators(song.id);
    setCollaborators(data as DbCollaborator[]);
  }, [song.id]);

  useEffect(() => { loadCollaborators(); }, [loadCollaborators]);

  const handleInvite = useCallback(async () => {
    if (!user || !email.trim()) return;
    setLoading(true);
    setError(null);
    setSuccess(null);

    const result = await inviteCollaborator(song.id, user.id, email.trim(), mode);
    setLoading(false);

    if (!result.ok) {
      setError(result.error ?? 'Failed to invite.');
    } else {
      setSuccess(`Invite sent to ${email.trim()}`);
      setEmail('');
      loadCollaborators();
    }
  }, [user, song.id, email, mode, loadCollaborators]);

  const handleRemove = useCallback(async (collaboratorId: string) => {
    await removeCollaborator(song.id, collaboratorId);
    loadCollaborators();
  }, [song.id, loadCollaborators]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 700, width: 460,
          background: 'var(--color-paper)',
          border: '1px solid var(--color-rule)',
          borderRadius: 10,
          boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          padding: '28px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 20, color: 'var(--color-ink-primary)', marginBottom: 2 }}>
              Share chart
            </h2>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-ink-muted)' }}>
              {song.title}
            </p>
          </div>
          <button onClick={onClose} className="control-btn">✕</button>
        </div>

        {/* Mode selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 8 }}>
            Sharing mode
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 7, textAlign: 'left',
                  border: `1.5px solid ${mode === m.value ? 'var(--color-accent)' : 'var(--color-rule)'}`,
                  background: mode === m.value ? 'rgba(var(--color-accent-rgb), 0.06)' : 'var(--color-paper-alt)',
                  cursor: 'pointer', transition: 'all 120ms',
                }}
              >
                {/* Radio dot */}
                <div style={{
                  width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${mode === m.value ? 'var(--color-accent)' : 'var(--color-rule)'}`,
                  background: mode === m.value ? 'var(--color-accent)' : 'transparent',
                  transition: 'all 120ms',
                }} />
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--color-ink-primary)' }}>
                    {m.label}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-ink-muted)', marginTop: 1 }}>
                    {m.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Invite form */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 8 }}>
            Invite by email
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
              placeholder="colleague@email.com"
              style={{
                flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12,
                padding: '8px 10px',
                background: 'var(--color-paper-alt)',
                border: '1px solid var(--color-rule)',
                borderRadius: 6, outline: 'none',
                color: 'var(--color-ink-primary)',
              }}
            />
            <button
              onClick={handleInvite}
              disabled={loading || !email.trim()}
              style={{
                padding: '8px 14px',
                background: 'var(--color-accent)', color: 'white',
                border: 'none', borderRadius: 6,
                fontFamily: 'var(--font-mono)', fontSize: 12,
                cursor: loading || !email.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !email.trim() ? 0.5 : 1,
              }}
            >
              {loading ? '…' : 'Invite'}
            </button>
          </div>

          {error && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-error)', marginTop: 6 }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-ink-secondary)', marginTop: 6 }}>
              {success}
            </div>
          )}
        </div>

        {/* Collaborator list */}
        {collaborators.length > 0 && (
          <div>
            <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 8 }}>
              People with access
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {collaborators.map((c) => {
                const colMode = ((c as any).mode ?? 'view') as ShareMode;
                return (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '7px 10px', borderRadius: 6,
                      background: 'var(--color-paper-alt)',
                      border: '1px solid var(--color-rule)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: 'var(--font-mono)', fontSize: 12,
                        color: 'var(--color-ink-primary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.email}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-ink-muted)', marginTop: 1 }}>
                        {MODE_LABELS[colMode]} · {c.accepted ? 'accepted' : 'pending'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemove(c.id)}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: 10,
                        color: 'var(--color-error)', background: 'none',
                        border: 'none', cursor: 'pointer', padding: '2px 4px',
                      }}
                      title="Remove access"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}